/**
 * Applying a livery to a loaded model.
 *
 * deck.gl's `ScenegraphLayer` takes its `scenegraph` prop either as a URL it loads itself,
 * or as an already-parsed glTF object. This module is the second path: it loads a GLB once,
 * paints the livery artwork onto a canvas, and hands back a glTF whose **livery panel
 * material** carries that canvas as its base colour texture.
 *
 * Why patch rather than ship one GLB per livery: geometry is the expensive part of these
 * files and a baked variant duplicates all of it per livery, while the artwork is the only
 * thing that differs. One model set plus one drawn canvas per livery also means adding a
 * livery never touches Blender.
 *
 * ── The two contracts this depends on ────────────────────────────────────────────────
 *
 * 1. **The panel exists and is unwrapped.** `mta-assets`' exporters carve the sidewall onto
 *    its own material (`R160_Livery` / `MAT_LIVERY`) and give it side-elevation UVs. Only
 *    L0 and L1 have it; L2 is one merged shell with its UV layer stripped, which is why a
 *    livery reaches that tier as `farColor` on the far shell instead.
 * 2. **loaders.gl resolves a texture by index into two parallel arrays.** `postProcessGLTF`
 *    reads `json.textures[i].source` as an index into `json.images`, and separately takes
 *    the DECODED image from `gltf.images[i]` (`_resolveImage`'s `preloadedImage`). So an
 *    injected texture has to append to both, at the same index. Appending to only the JSON
 *    yields a texture whose image is null and a material that silently draws untextured.
 */
import { load } from '@loaders.gl/core'
import { GLTFLoader } from '@loaders.gl/gltf'
import { VENDORED_GLB_LOAD_OPTIONS } from './gltfLoadOptions'
import {
  LIVERY_CANVAS_H,
  LIVERY_CANVAS_W,
  LIVERY_CAP_H,
  LIVERY_CAP_W,
  LIVERY_FLANK_H,
  liverySurfaces,
  type LiveryBody,
  type LiveryFlank,
  type LiveryPaint,
  type LiverySurface,
} from './liveries'

type AnyGltf = {
  json: Record<string, any>
  images?: unknown[]
  [key: string]: unknown
}

export type LiveryTarget = {
  /**
   * Which BODY this model is, not which fleet — `r160-a` and `r160-b` are two vehicles, and
   * the agency scheme is a different drawing on each (a cab car has an operator's window
   * where the other has a passenger bay). It is handed straight to `LiveryPaint.draw`.
   *
   * This is free to carry, because the patch cache is already keyed by model URL and the
   * URLs differ per body: the A car and the B car were always getting their own canvas, and
   * until now the two were simply drawn identically.
   */
  body: LiveryBody
  /**
   * Which material carries each livery surface on this body — `R160_LIVERY_SURFACES` or
   * `XCEL40_LIVERY_SURFACES`. A surface the body does not have is simply absent, and a
   * livery asking for one is ignored rather than failing: the train has no nose to paint.
   *
   * All of them are absent at L2, and that is not an error either.
   */
  surfaceMaterials: Partial<Record<LiverySurface, string>>
  /** The merged far-shell material. Present only at L2, and likewise not an error. */
  farShellMaterial: string
  /**
   * The body's leftover blockout material — roof, wheel arches, skirt returns — which a
   * livery can COLOUR but not draw on, the faces having no usable UVs. Absent for bodies
   * that do not split one out. See `shellColor` on `LiveryPaint` for why it matters more
   * than it sounds: this map looks straight down, so the roof is what it mostly shows.
   */
  shellMaterial?: string
}

/**
 * Resolved, cached, stable-by-identity scenegraphs.
 *
 * ⚠️ Identity is the whole point. `buildVehicleLayers` runs up to 30 times a second and
 * deck diffs `scenegraph` by reference — hand it a freshly patched object each frame and it
 * rebuilds every model, uploads every buffer and re-parses every material, forever. So the
 * patched glTF is built once per (url, livery) and then returned from this map by identity.
 */
const patched = new Map<string, AnyGltf>()
/**
 * The load+patch currently running for a key, as a PROMISE rather than a bare marker.
 *
 * The map never waits — it draws the plain model and picks the patch up next frame — but the
 * picker's 3D swatch has no next frame to fall back on: it renders once and captures the
 * pixels. Holding the promise lets `liveryScenegraphReady` await exactly the work the frame
 * loop already kicked off, so a livery is never loaded or patched twice.
 */
const inFlight = new Map<string, Promise<AnyGltf | null>>()

/** Loads and patches once per key, whoever asks first. Resolves `null` if either step fails. */
function patchOnce(
  url: string,
  paint: LiveryPaint,
  target: LiveryTarget,
  key: string,
): Promise<AnyGltf | null> {
  const running = inFlight.get(key)
  if (running) return running
  const job = load(url, GLTFLoader, VENDORED_GLB_LOAD_OPTIONS)
    .then((gltf: unknown) => {
      const out = applyPaint(gltf as AnyGltf, paint, target)
      patched.set(key, out)
      return out
    })
    // Leave the entry unset: the caller keeps drawing the plain model, which is the right
    // failure. A livery is paint, and losing the paint must not lose the vehicle.
    .catch(() => null)
    .finally(() => inFlight.delete(key))
  inFlight.set(key, job)
  return job
}

/**
 * Paints both flank islands from one `draw`.
 *
 * The exporters unwrap each side of the vehicle onto its own stacked island — the physical
 * LEFT is the sheet's top half — so the sheet is two side elevations, not one. A livery
 * author should never have to know that: `draw` is called once per island with the origin
 * translated onto it and a clip applied, so it paints a plain side view in `w × h` and
 * cannot bleed into the other flank however it fills its canvas.
 *
 * The `flank` argument exists for the one case that genuinely differs between the two —
 * a handed marking, like the R160-A's US flag, which sits at a different station on each
 * side of the car. Ordinary artwork ignores it and comes out identical on both sides.
 *
 * `body` is the same argument one level up, and is the caller's: which of the fleet's bodies
 * this canvas is for. The R160-A and R160-B share a fleet, a panel and a sheet, and wear
 * different artwork under the agency scheme.
 */
function drawLivery(paint: LiveryPaint, body: LiveryBody): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = LIVERY_CANVAS_W
  canvas.height = LIVERY_CANVAS_H
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  const islands: ReadonlyArray<[LiveryFlank, number]> = [
    ['left', 0],
    ['right', LIVERY_FLANK_H],
  ]
  for (const [flank, top] of islands) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(0, top, LIVERY_CANVAS_W, LIVERY_FLANK_H)
    ctx.clip()
    ctx.translate(0, top)
    paint.draw(ctx, LIVERY_CANVAS_W, LIVERY_FLANK_H, flank, body)
    ctx.restore()
  }
  return canvas
}

/** One end cap's own elevation, on its own square sheet. See `LIVERY_CAP_W`. */
function drawCap(render: (ctx: CanvasRenderingContext2D, w: number, h: number) => void) {
  const canvas = document.createElement('canvas')
  canvas.width = LIVERY_CAP_W
  canvas.height = LIVERY_CAP_H
  const ctx = canvas.getContext('2d')
  if (ctx) render(ctx, LIVERY_CAP_W, LIVERY_CAP_H)
  return canvas
}

/**
 * The image each surface samples.
 *
 * ⚠️ `sidewall`, `doors` and `windows` deliberately share ONE canvas, because they share
 * one unwrap: the exporters project all three against the sidewall's extent so a stripe
 * runs across a door leaf unbroken. Giving them a texture each would draw the whole side
 * elevation three times over, in miniature, on each surface.
 */
function surfaceCanvases(
  paint: LiveryPaint, body: LiveryBody,
): Map<LiverySurface, HTMLCanvasElement> {
  const canvases = new Map<LiverySurface, HTMLCanvasElement>()
  const wanted = liverySurfaces(paint)
  const flank = drawLivery(paint, body)
  for (const surface of ['sidewall', 'doors', 'windows'] as const) {
    if (wanted.includes(surface)) canvases.set(surface, flank)
  }
  if (wanted.includes('front') && paint.drawFront) {
    canvases.set('front', drawCap(paint.drawFront))
  }
  if (wanted.includes('rear') && paint.drawRear) {
    canvases.set('rear', drawCap(paint.drawRear))
  }
  return canvases
}

function applyPaint(gltf: AnyGltf, paint: LiveryPaint, target: LiveryTarget): AnyGltf {
  // Clone only what is written to. The buffers and accessors — every byte of geometry — are
  // shared with the unliveried model on purpose: two liveries of one body differ by a
  // material and an image, and copying the mesh to change its paint would defeat the point
  // of patching rather than baking.
  const json: Record<string, any> = { ...gltf.json }
  const materials = (json.materials ?? []).map((m: any) => ({ ...m }))
  json.materials = materials

  const farIndex = materials.findIndex((m: any) => m.name === target.farShellMaterial)

  const images = [...(json.images ?? [])]
  const decoded = [...((gltf.images as unknown[]) ?? [])]
  const samplers = [...(json.samplers ?? [])]
  const textures = [...(json.textures ?? [])]
  // One texture per distinct CANVAS, not per surface: the three flank surfaces share one
  // image, so a bus wrapped on body, doors and glazing uploads one side elevation rather
  // than three copies of it.
  const textureOf = new Map<HTMLCanvasElement, number>()
  let painted = 0

  for (const [surface, canvas] of surfaceCanvases(paint, target.body)) {
    const name = target.surfaceMaterials[surface]
    if (!name) continue
    const index = materials.findIndex((m: any) => m.name === name)
    if (index < 0) continue

    let texture = textureOf.get(canvas)
    if (texture === undefined) {
      // Both arrays get one entry, at the same index — see contract 2 above.
      images.push({ name: `livery-${surface}` })
      decoded.push(canvas)
      // Clamp, not repeat: the artwork is the whole side of the vehicle, so anything
      // sampling outside [0,1] is a rounding error at the panel's edge, and wrapping it
      // there would fetch the far end of the livery and draw a seam down the nose.
      samplers.push({ magFilter: 9729, minFilter: 9987, wrapS: 33071, wrapT: 33071 })
      textures.push({ sampler: samplers.length - 1, source: images.length - 1 })
      texture = textures.length - 1
      textureOf.set(canvas, texture)
    }

    const material = materials[index]
    const pbr = { ...(material.pbrMetallicRoughness ?? {}) }
    pbr.baseColorTexture = { index: texture, texCoord: 0 }
    // The surface's authored base colour multiplies the texture. It is whatever it was cut
    // from — bare stainless on the R160's panel, near-black on the bus's door leaves —
    // which would tint or swallow every livery, so the artwork gets a white multiplier and
    // speaks for itself.
    pbr.baseColorFactor = [1, 1, 1, 1]
    pbr.metallicFactor = paint.finish.metallic
    pbr.roughnessFactor = paint.finish.roughness
    material.pbrMetallicRoughness = pbr
    painted++
  }

  if (painted) {
    json.images = images
    json.samplers = samplers
    json.textures = textures
    gltf = { ...gltf, images: decoded }
  }

  if (farIndex >= 0) {
    const far = materials[farIndex]
    const pbr = { ...(far.pbrMetallicRoughness ?? {}) }
    pbr.baseColorFactor = [...paint.farColor, 1]
    far.pbrMetallicRoughness = pbr
  }

  // The roof and the rest of the blockout body: a colour, never a picture. A livery that
  // does not ask for one leaves it exactly as authored, which is right for the R160 (bare
  // stainless is what its roof really is) and wrong for a painted bus — see `shellColor`.
  const shellIndex = target.shellMaterial
    ? materials.findIndex((m: any) => m.name === target.shellMaterial)
    : -1
  if (paint.shellColor && shellIndex >= 0) {
    const shell = materials[shellIndex]
    const pbr = { ...(shell.pbrMetallicRoughness ?? {}) }
    pbr.baseColorFactor = [...paint.shellColor, 1]
    // Match the paint beside it: the roof of a painted bus is painted, and leaving the
    // blockout's own roughness there puts a different finish on the lid than the flank.
    pbr.metallicFactor = paint.finish.metallic
    pbr.roughnessFactor = paint.finish.roughness
    shell.pbrMetallicRoughness = pbr
  }

  return { ...gltf, json }
}

/**
 * The scenegraph prop for this model under this livery.
 *
 * Synchronous by design, because the caller is a per-frame layer builder. It returns the
 * plain `url` — the unliveried model deck loads itself — until the patched version is ready,
 * then the patched object from then on. A livery therefore appears a beat after it is
 * chosen rather than blanking the fleet while it loads, and swapping the prop from string to
 * object costs exactly one layer rebuild.
 *
 * `paint === null` is the as-built livery and returns the URL forever, so the default path
 * never loads, patches or caches anything.
 */
export function liveryScenegraph(
  url: string,
  paint: LiveryPaint | null,
  target: LiveryTarget,
  liveryId: string,
): string | AnyGltf {
  if (!paint) return url
  const key = `${liveryId}|${url}`
  const ready = patched.get(key)
  if (ready) return ready
  void patchOnce(url, paint, target, key)
  return url
}

/**
 * The same thing, awaited — for the one caller that cannot come back next frame.
 *
 * `liveryPreview.ts` renders a vehicle to a still image, so "draw it plain and pick the paint
 * up later" would capture the unpainted model and keep it forever. It shares this module's
 * cache in both directions: a livery previewed in the picker is already patched when a rider
 * chooses it, and one already on the map is previewed without a second load.
 *
 * Resolves to the plain `url` if the load or the patch fails, which is the same failure the
 * synchronous path takes — the vehicle still draws, unpainted.
 */
export async function liveryScenegraphReady(
  url: string,
  paint: LiveryPaint | null,
  target: LiveryTarget,
  liveryId: string,
): Promise<string | AnyGltf> {
  if (!paint) return url
  const key = `${liveryId}|${url}`
  const ready = patched.get(key)
  if (ready) return ready
  return (await patchOnce(url, paint, target, key)) ?? url
}

/** Test seam: drops every cached patch so a run starts from nothing. */
export function resetLiveryCache(): void {
  patched.clear()
  inFlight.clear()
}
